import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  Alert,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material'
import { 
  Science as ScienceIcon, 
  Add as AddIcon,
  Timeline as TimelineIcon,
  SaveAlt as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import Layout from '../components/Layout/Layout'
import { usePool } from '../contexts/PoolContext'
import { useMeasurements } from '../hooks/useMeasurements'
import { useChemicalAdditions } from '../hooks/useChemicalAdditions'
import PoolSelector from '../components/Pool/PoolSelector'
import ChemicalAdditionForm from '../components/Chemicals/ChemicalAdditionForm'
import StatusBadge from '../components/Common/StatusBadge'
import {
  calculateTargetRanges,
  calculateRecommendations,
  getParameterStatus,
  getStatusColor,
  getStatusText,
  calculateChemicalImpact,
  getStatusTooltip,
  formatAmount
} from '../utils/chemicalCalculations'
import { CHEMICAL_DATA } from '../utils/constants'
import usePageTracking from '../hooks/usePageTracking'

const AddChemicals = () => {
  usePageTracking('Add Chemicals')
  
  const navigate = useNavigate()
  const { pools, selectedPool, setSelectedPool } = usePool()
  const [currentPool, setCurrentPool] = useState(selectedPool)
  const { getLatestMeasurement } = useMeasurements(currentPool?.id)
  const { createChemicalAddition, chemicalAdditions } = useChemicalAdditions(currentPool?.id)
  const [showForm, setShowForm] = useState(false)
  const [selectedRecommendation, setSelectedRecommendation] = useState(null)
  const [additionLoading, setAdditionLoading] = useState(false)
  const [chemicalsToAdd, setChemicalsToAdd] = useState([])
  const [selectedNewChemical, setSelectedNewChemical] = useState('')
  const [saveAllLoading, setSaveAllLoading] = useState(false)

  const latestMeasurement = getLatestMeasurement()

  // Auto-select single pool or use pre-selected pool
  useEffect(() => {
    if (pools.length === 1) {
      setCurrentPool(pools[0])
    } else if (selectedPool) {
      setCurrentPool(selectedPool)
    }
  }, [pools, selectedPool])

  const targetRanges = useMemo(() => {
    if (!currentPool || !latestMeasurement) return null
    return calculateTargetRanges(currentPool, latestMeasurement)
  }, [currentPool, latestMeasurement])

  const recommendations = useMemo(() => {
    if (!currentPool || !latestMeasurement || !targetRanges) return []
    return calculateRecommendations(currentPool, latestMeasurement, targetRanges)
  }, [currentPool, latestMeasurement, targetRanges])

  // Find chemicals added after the latest measurement
  const chemicalsAddedAfterMeasurement = useMemo(() => {
    if (!latestMeasurement || !chemicalAdditions.length) return []
    
    const measurementDate = new Date(latestMeasurement.measured_at)
    return chemicalAdditions.filter(addition => {
      const additionDate = new Date(addition.added_at)
      return additionDate > measurementDate
    })
  }, [latestMeasurement, chemicalAdditions])

  // Initialize chemicals to add with recommendations (only if no chemicals added after measurement)
  useEffect(() => {
    if (chemicalsAddedAfterMeasurement.length > 0) {
      // Don't show recommendations if chemicals were added after measurement
      setChemicalsToAdd([])
    } else if (recommendations.length > 0) {
      const initialChemicals = recommendations.map((rec, index) => ({
        id: `rec-${index}`,
        ...rec,
        isRecommended: true,
        isEditing: false
      }))
      setChemicalsToAdd(initialChemicals)
    } else {
      setChemicalsToAdd([])
    }
  }, [recommendations, chemicalsAddedAfterMeasurement])

  const parameterStatuses = useMemo(() => {
    if (!latestMeasurement || !targetRanges) return []
    
    const parameters = [
      { 
        key: 'total_chlorine', 
        label: 'TC', 
        unit: 'ppm',
        calculate: (m) => (m.free_chlorine || 0) + (m.combined_chlorine || 0),
        useRange: 'free_chlorine' // Use free chlorine target ranges for status
      },
      { key: 'ph', label: 'pH', unit: '' },
      { key: 'total_alkalinity', label: 'TA', unit: 'ppm' },
      { key: 'cya_stabilizer', label: 'CYA', unit: 'ppm' },
      { key: 'calcium_hardness', label: 'CH', unit: 'ppm' }
    ]

    return parameters.map(param => {
      const value = param.calculate ? param.calculate(latestMeasurement) : latestMeasurement[param.key]
      const range = targetRanges[param.useRange || param.key]
      const status = getParameterStatus(value, range)
      const paramKey = param.useRange || param.key
      
      return {
        ...param,
        value,
        range,
        status,
        statusText: getStatusText(status),
        statusColor: getStatusColor(status),
        tooltip: getStatusTooltip(paramKey, status, range, value, currentPool, latestMeasurement)
      }
    })
  }, [latestMeasurement, targetRanges, currentPool])

  const handlePoolChange = (pool) => {
    setCurrentPool(pool)
    setSelectedPool(pool)
  }

  const handleEditChemical = (chemical) => {
    setSelectedRecommendation(chemical)
    setShowForm(true)
  }

  const handleDeleteChemical = (chemicalId) => {
    setChemicalsToAdd(prev => prev.filter(chem => chem.id !== chemicalId))
  }

  const handleAddNewChemical = () => {
    if (!selectedNewChemical || !currentPool || !latestMeasurement) return
    
    const chemicalData = CHEMICAL_DATA[selectedNewChemical]
    if (!chemicalData) return

    const newChemical = {
      id: `new-${Date.now()}`,
      chemical: selectedNewChemical,
      chemicalName: chemicalData.name,
      amount: 1, // Default amount
      unit: chemicalData.unit,
      isRecommended: false,
      isEditing: false,
      issue: 'Manual addition',
      priority: 'low',
      expectedEffect: 'To be calculated based on amount'
    }

    setChemicalsToAdd(prev => [...prev, newChemical])
    setSelectedNewChemical('')
  }


  const availableChemicals = Object.keys(CHEMICAL_DATA)
    .filter(key => !chemicalsToAdd.some(chem => chem.chemical === key))

  const handleSaveAddition = async (additionData) => {
    setAdditionLoading(true)
    try {
      // Don't save to database yet, just update the staged chemical
      const updatedChemicals = chemicalsToAdd.map(chem => {
        if (chem.id === selectedRecommendation.id) {
          return {
            ...chem,
            amount: additionData.amount,
            notes: additionData.notes,
            added_at: additionData.added_at,
            // Recalculate impact with new amount
            expectedEffect: calculateChemicalImpact(
              chem.chemical,
              additionData.amount,
              currentPool,
              latestMeasurement
            ).description || chem.expectedEffect
          }
        }
        return chem
      })
      
      setChemicalsToAdd(updatedChemicals)
      setShowForm(false)
      setSelectedRecommendation(null)
    } catch (error) {
      throw error
    } finally {
      setAdditionLoading(false)
    }
  }

  const handleCancelAddition = () => {
    setShowForm(false)
    setSelectedRecommendation(null)
  }

  const handleSaveAllChemicals = async () => {
    if (chemicalsToAdd.length === 0) return
    
    setSaveAllLoading(true)
    try {
      // Save all chemicals to database
      const savePromises = chemicalsToAdd.map(chemical => 
        createChemicalAddition({
          chemical: chemical.chemical,
          chemicalName: chemical.chemicalName,
          amount: chemical.amount,
          unit: chemical.unit,
          added_at: chemical.added_at || new Date().toISOString(),
          notes: chemical.notes || '',
          measurementId: latestMeasurement?.id,
          recommendation: chemical.isRecommended ? chemical : null
        })
      )
      
      const results = await Promise.all(savePromises)
      
      // Check for any errors
      const errors = results.filter(result => result.error)
      if (errors.length > 0) {
        throw new Error(`Failed to save ${errors.length} chemical addition(s)`)
      }
      
      // Clear staged chemicals and redirect to dashboard
      setChemicalsToAdd([])
      navigate('/')
    } catch (error) {
      console.error('Error saving chemical additions:', error)
      alert('Error saving chemical additions: ' + error.message)
    } finally {
      setSaveAllLoading(false)
    }
  }

  const handleCreatePool = () => {
    navigate('/pools')
  }

  const formatDate = (dateString) => new Date(dateString).toLocaleString()

  const formatValue = (value, unit) => {
    if (value === null || value === undefined) return '—'
    return `${typeof value === 'number' ? value.toFixed(unit === '' ? 1 : 0) : value}${unit ? ` ${unit}` : ''}`
  }

  // Show pool selection if no pools exist
  if (pools.length === 0) {
    return (
      <Layout>
        <Box sx={{ textAlign: 'center', py: { xs: 6, md: 12 } }}>
          <ScienceIcon
            sx={{
              fontSize: { xs: 48, md: 64 },
              color: 'text.disabled',
              mb: 2
            }}
          />
          <Typography variant="h5" component="h2" gutterBottom>
            No Pools Found
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            You need to create a pool before adding chemicals.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreatePool}
            size="large"
            sx={{ 
              borderRadius: 2,
              py: 1.5,
              px: 4,
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            Create Your First Pool
          </Button>
        </Box>
      </Layout>
    )
  }

  return (
    <Layout>
      <Box sx={{ maxWidth: 'md', mx: 'auto', p: 2 }}>
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            borderRadius: 3,
            mb: 3
          }}
        >
          <Stack spacing={3}>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                Add Chemicals
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Add chemicals based on your latest measurements
              </Typography>
            </Box>

            <PoolSelector
              selectedPool={currentPool}
              onPoolChange={handlePoolChange}
              label="Pool"
              showAlert={true}
            />
          </Stack>
        </Paper>

        {currentPool && (
          <>
            {/* Latest Measurement Summary */}
            {latestMeasurement ? (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                      Latest Measurements
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(latestMeasurement.measured_at)}
                    </Typography>
                  </Box>
                  
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {parameterStatuses.map((param) => (
                      <Grid item xs={6} sm={4} md={2.4} key={param.key}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                            {formatValue(param.value, param.unit)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                            {param.label}
                          </Typography>
                          <StatusBadge
                            label={param.statusText}
                            color={param.statusColor}
                            size="small"
                            tooltip={param.tooltip}
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      Not the most recent measurements?{' '}
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => navigate('/add-measurement')}
                        sx={{ textTransform: 'none', p: 0, minWidth: 'auto', textDecoration: 'underline' }}
                      >
                        Add a new measurement
                      </Button>
                      {' '}to see updated chemical recommendations.
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    No measurements found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    You need to add a measurement before getting chemical recommendations.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/add-measurement')}
                    sx={{ 
                      borderRadius: 2,
                      py: 1.5,
                      textTransform: 'none',
                      fontSize: '1rem'
                    }}
                  >
                    Add Measurement First
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Chemical Additions Management */}
            <Card>
              <CardContent>
                <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  Chemical Additions
                </Typography>

                {/* Warning for chemicals added after measurement */}
                {chemicalsAddedAfterMeasurement.length > 0 && (
                  <Alert severity="warning" sx={{ borderRadius: 2, mb: 3 }}>
                    <Typography variant="body2">
                      <strong>Note:</strong> The following chemicals were added after this measurement:{' '}
                      {chemicalsAddedAfterMeasurement.map((addition, index) => (
                        <span key={addition.id}>
                          {addition.chemical_types?.name || 'Unknown Chemical'} 
                          ({formatAmount(addition.amount, addition.unit)})
                          {index < chemicalsAddedAfterMeasurement.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                      . Consider taking a new measurement for accurate recommendations.
                    </Typography>
                  </Alert>
                )}
                
                {chemicalsToAdd.length > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Review and adjust chemical additions. Click "Edit" to modify amounts.
                    </Typography>

                    <Stack spacing={2} sx={{ mb: 3 }}>
                      {chemicalsToAdd.map((chem) => (
                        <Card key={chem.id} variant="outlined" sx={{ 
                          borderLeft: `4px solid ${
                            chem.priority === 'high' ? '#f44336' : 
                            chem.priority === 'medium' ? '#ff9800' : 
                            chem.isRecommended ? '#2196f3' : '#9e9e9e'
                          }`
                        }}>
                          <CardContent sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {chem.chemicalName}
                                  {chem.isRecommended && (
                                    <Typography component="span" variant="caption" sx={{ 
                                      ml: 1, 
                                      px: 1, 
                                      py: 0.25, 
                                      bgcolor: 'primary.main', 
                                      color: 'white', 
                                      borderRadius: 1,
                                      fontSize: '0.7rem'
                                    }}>
                                      RECOMMENDED
                                    </Typography>
                                  )}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {chem.issue}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Amount: {formatAmount(chem.amount, chem.unit)} • {chem.expectedEffect}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<EditIcon />}
                                  onClick={() => handleEditChemical(chem)}
                                  sx={{ 
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  Edit
                                </Button>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteChemical(chem.id)}
                                  sx={{ 
                                    border: '1px solid',
                                    borderColor: 'error.main',
                                    '&:hover': {
                                      bgcolor: 'error.light'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  </>
                )}

                {/* Add New Chemical Dropdown */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3 }}>
                  <FormControl sx={{ minWidth: 250 }}>
                    <InputLabel>Add Another Chemical</InputLabel>
                    <Select
                      value={selectedNewChemical}
                      label="Add Another Chemical"
                      onChange={(e) => setSelectedNewChemical(e.target.value)}
                    >
                      {availableChemicals.map((chemKey) => (
                        <MenuItem key={chemKey} value={chemKey}>
                          {CHEMICAL_DATA[chemKey].name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddNewChemical}
                    disabled={!selectedNewChemical}
                    sx={{ 
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Add Chemical
                  </Button>
                </Box>

                {chemicalsToAdd.length === 0 && chemicalsAddedAfterMeasurement.length === 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ScienceIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom color="success.main">
                      {latestMeasurement ? 'Water Chemistry Looks Good!' : 'No Measurements Available'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {latestMeasurement 
                        ? 'All parameters are within target ranges. Add chemicals manually if needed.' 
                        : 'Add a measurement to see chemical recommendations.'}
                    </Typography>
                  </Box>
                )}

                {chemicalsToAdd.length === 0 && chemicalsAddedAfterMeasurement.length > 0 && (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ScienceIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h6" gutterBottom color="warning.main">
                      Chemicals Already Added
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Since chemicals were added after your last measurement, we're not showing calculated recommendations. 
                      You can still add chemicals manually using the dropdown below.
                    </Typography>
                  </Box>
                )}

                {/* Save All Button */}
                {chemicalsToAdd.length > 0 && (
                  <Box sx={{ textAlign: 'center', pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={saveAllLoading ? null : <SaveIcon />}
                      onClick={handleSaveAllChemicals}
                      disabled={saveAllLoading}
                      sx={{ 
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5
                      }}
                    >
                      {saveAllLoading ? 'Saving...' : 'Save All Chemical Additions'}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>

          </>
        )}

        {/* Chemical Addition Form Dialog */}
        <ChemicalAdditionForm
          open={showForm}
          onClose={handleCancelAddition}
          onSave={handleSaveAddition}
          recommendation={selectedRecommendation}
          loading={additionLoading}
          poolConfig={currentPool}
          currentMeasurement={latestMeasurement}
        />
      </Box>
    </Layout>
  )
}

export default AddChemicals